import { Box, Flex, Link, Button, Alert, Text, Spinner } from '@televet/kibble-ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RRLink } from 'react-router-dom';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useGetClinicPostcardsSettingsQuery } from 'shared/types/graphql';
import { getEnvironment } from 'shared/utils/http';
import styled from 'styled-components/macro';
import DisablePostcardsAlertDialog from './DisablePostcardsAlert';

interface IDocupostMessage {
  func: string;
  message: string;
}

enum DocupostFunctions {
  SignUp = 'docupost-signup',
}

enum DocupostMessages {
  Success = 'success',
}

const PostcardsSection = (): JSX.Element => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { currentClinicId, currentClinic } = useClinicUser();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const env = getEnvironment();
  const docupostSignUpUrl = useMemo(
    () => `https://docupost.com/televet_start?clinicId=${currentClinicId}&env=${env}`,
    [currentClinicId, env],
  );

  const showErrorMessage = useMemo(() => {
    if (currentClinic) {
      const { city, addressLine1, state, postalCode, logo } = currentClinic;
      return !(city && addressLine1 && state && postalCode && logo);
    }
    return false;
  }, [currentClinic]);

  const {
    data,
    loading: loadingSettings,
    refetch: refetchSettings,
  } = useGetClinicPostcardsSettingsQuery({
    variables: {
      where: {
        clinicId: currentClinicId,
      },
    },
    skip: !currentClinicId,
  });

  const settings = data?.findUniqueClinicPostcardSetting;
  const isEnabled = settings ? settings.isRemindersEnabled : true;
  const docupostConfigurationUrl = `https://docupost.com/televet_manage?key=${settings?.apiKey}&secret=${settings?.apiSecret}`;
  const shouldShowErrorMessage = isEnabled && showErrorMessage;

  useEffect(() => {
    const handleMessageEvent = (event: MessageEvent): void => {
      if (!iframeRef.current) {
        return;
      }

      if (event.origin !== 'https://app.docupost.com' || event.source !== iframeRef.current.contentWindow) {
        return;
      }

      const data = event.data as IDocupostMessage;

      if (data.func === DocupostFunctions.SignUp && data.message === DocupostMessages.Success) {
        refetchSettings();
      }
    };
    window.addEventListener('message', handleMessageEvent);

    return (): void => {
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [refetchSettings]);

  if (loadingSettings) {
    return <Spinner />;
  }

  return (
    <Box height="100%" pt={5}>
      {settings && (
        <Flex alignItems="center" justifyContent={shouldShowErrorMessage ? 'space-between' : 'end'} width="100%">
          {shouldShowErrorMessage && (
            <Alert
              status="error"
              maxWidth="640px"
              title="Unable to send postcards"
              description={
                <Text ml={9}>
                  In order to send postcards, please make sure your logo and clinic address are listed correctly in your{' '}
                  <RRLink to={`${RouteBasePaths.Settings}${SettingsRoutes.ClinicGeneral}`}>
                    general settings page
                  </RRLink>
                  .
                </Text>
              }
            />
          )}
          <Flex alignItems="center" justifyContent="end">
            <Link href="https://docupost.com/postcards" mr={8} fontWeight="bold" isExternal>
              View mailed postcards
            </Link>
            {isEnabled ? (
              <Button onClick={(): void => setIsAlertOpen(true)} variant="ghost">
                Disable postcards
              </Button>
            ) : (
              <Button onClick={(): void => setIsAlertOpen(true)}>Enable postcards</Button>
            )}
          </Flex>
        </Flex>
      )}
      {isEnabled && !loadingSettings && (
        <StyledIframe src={settings ? docupostConfigurationUrl : docupostSignUpUrl} ref={iframeRef} />
      )}
      <DisablePostcardsAlertDialog
        isOpen={isAlertOpen}
        onClose={(): void => setIsAlertOpen(false)}
        isPostcardsEnabled={isEnabled}
        settingsId={settings?.id || ''}
      />
    </Box>
  );
};

export default PostcardsSection;

const StyledIframe = styled.iframe`
  height: 100%;
  margin-top: 30px;
  min-height: 600px;
  width: 100%;
`;
